import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Event } from '../types';
import EventCard from './EventCard';
import styles from './LiveCarousel.module.css';

interface LiveCarouselProps {
    events: Event[];
    isFavorite: (id: string) => boolean;
    hasUpvoted: (id: string) => boolean;
    onUpvote: (e: React.MouseEvent, eventId: string) => void;
    onToggleFavorite: (e: React.MouseEvent, eventId: string) => void;
}

const LiveCarousel: React.FC<LiveCarouselProps> = ({
    events,
    isFavorite,
    hasUpvoted,
    onUpvote,
    onToggleFavorite,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setCanScrollLeft(el.scrollLeft > 8);
        setCanScrollRight(maxScroll > 8 && el.scrollLeft < maxScroll - 8);
    }, []);

    const scroll = (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        const card = el.querySelector<HTMLElement>(`.${styles.cardWrap}`);
        const step = (card?.offsetWidth || 280) + 12;
        el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
    };

    useEffect(() => {
        const timer = window.setTimeout(updateScrollState, 80);
        const el = scrollRef.current;
        if (!el) return () => window.clearTimeout(timer);

        el.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);
        return () => {
            window.clearTimeout(timer);
            el.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
    }, [events.length, updateScrollState]);

    if (events.length === 0) return null;

    return (
        <section className={styles.section} aria-label="লাইভ ম্যাচ">
            <div className={styles.header}>
                <h2 className={styles.title}>
                    <span className={styles.liveDot} aria-hidden="true" />
                    এখন লাইভ
                    <span className={styles.count}>{events.length}</span>
                </h2>
                {events.length > 1 && (
                    <span className={styles.hint} aria-hidden="true">← সোয়াইপ করুন →</span>
                )}
            </div>

            <div className={styles.carousel}>
                {canScrollLeft && (
                    <button
                        type="button"
                        className={`${styles.arrow} ${styles.arrowLeft}`}
                        onClick={() => scroll('left')}
                        aria-label="আগের লাইভ ম্যাচ"
                    >
                        <span aria-hidden="true">‹</span>
                    </button>
                )}

                <div
                    ref={scrollRef}
                    className={styles.track}
                    role="list"
                    aria-label="লাইভ ম্যাচ তালিকা"
                >
                    {events.map((event) => (
                        <div key={event.id} className={styles.cardWrap} role="listitem">
                            <EventCard
                                event={event}
                                variant="carousel"
                                isFavorite={isFavorite(event.id)}
                                hasUpvoted={hasUpvoted(event.id)}
                                onUpvote={onUpvote}
                                onToggleFavorite={onToggleFavorite}
                            />
                        </div>
                    ))}
                </div>

                {canScrollRight && (
                    <button
                        type="button"
                        className={`${styles.arrow} ${styles.arrowRight}`}
                        onClick={() => scroll('right')}
                        aria-label="পরের লাইভ ম্যাচ"
                    >
                        <span aria-hidden="true">›</span>
                    </button>
                )}

                {canScrollRight && <div className={styles.fadeRight} aria-hidden="true" />}
                {canScrollLeft && <div className={styles.fadeLeft} aria-hidden="true" />}
            </div>
        </section>
    );
};

export default LiveCarousel;
